import React, { useEffect, useId, useRef, useState } from 'react';
import { PERSONEN } from './cockpitTexte';
import { TippBox, type TippInhalt } from './Diagramm';
import { zahl } from './format';
import './tokenBalken.css';

// Profil-Nachbau F-25 (Nachtrag 16:24, AUFTRAG-PF-GC-person-1): „Verbrauchte Tokens je Person“ als waagrechte Balken über der Tabelle
// „Je Person“. Der Baustein Diagramm kennt nur Säulen und Linien, deshalb eine schlichte Balkenliste wie BalkenListe
// (BerichtBausteine.tsx), je Zeile ein Knopf mit Tooltip per Maus, Finger und Tastatur (Gestalt TippBox). Die Zeilen stehen in der
// Reihenfolge des Servers (ohne Namen nach Kennung, mit Namen nach Namen), nie nach Menge: keine Rangliste. Die Tokens kommen vom
// Server schon auf zwei geltende Stellen gerundet; diese Datei kennt keinen Betrag und keine Person außer der gelieferten Bezeichnung.

export type TokenZeile = { key: string; name: string; tokens: number | null; recherchen: number | null; nachfragen: number | null; tokensOhneAngabe?: number };

/** Tokens in de-AT: ab einer Million „1,2 Mio.“, darunter mit Tausenderpunkt „840.000“. */
export function tokenZahl(token: number | null): string {
  if (token === null) return '–';
  if (!Number.isFinite(token) || token <= 0) return '0';
  return token >= 1000000 ? PERSONEN.tokensMio(zahl(Math.round(token / 100000) / 10)) : zahl(Math.round(token));
}

/** Länge je Balken in Prozent des größten Werts; ein Wert über 0 bleibt sichtbar. */
export function balkenProzent(werte: number[]): number[] {
  const hoechst = Math.max(0, ...werte);
  return werte.map(wert => hoechst <= 0 || wert <= 0 ? 0 : Math.max(1, Math.min(100, wert * 100 / hoechst)));
}

/** Tooltip einer Zeile: Recherchen und Nachfragen; mit fehlenden Angaben der Hinweis als Fuß. Seit PF-SL5-tokens-1 ohne Eingabe und
 *  Ausgabe: Firmen sehen nur die Summe (Negativliste 3.9). */
export function tokenTipp(z: TokenZeile): TippInhalt {
  return { kopf: z.name, zeilen: [...(z.recherchen !== null ? [{ name: PERSONEN.spalten.recherchen, wert: zahl(z.recherchen) }] : []),
    ...(z.nachfragen !== null ? [{ name: PERSONEN.spalten.nachfragen, wert: zahl(z.nachfragen) }] : [])],
    ...(z.tokensOhneAngabe ? { fuss: PERSONEN.tokensOhneAngabe(z.tokensOhneAngabe) } : {}) };
}

export default function TokenBalken({ zeilen, leer }: { zeilen: TokenZeile[]; leer: string }) {
  const [offen, setOffen] = useState<string | null>(null);
  const offenRef = useRef<string | null>(null);
  const knoepfe = useRef(new Map<string, HTMLButtonElement>());
  const tippId = `${useId()}-tipp`;
  const oeffnen = (key: string) => { offenRef.current = key; setOffen(key); };
  const schliessen = useRef(() => { offenRef.current = null; setOffen(null); }).current;

  // Escape schließt nur den Tooltip, nicht das Fenster der Bereichsseite (wie Bericht in BerichtBausteine.tsx); ein Antippen daneben
  // schließt ihn am Handy, wo ein Knopf beim Antippen keinen Fokus bekommt.
  useEffect(() => {
    if (!offen) return;
    const taste = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault(); event.stopPropagation();
      schliessen();
    };
    const daneben = (event: PointerEvent) => {
      const knopf = offenRef.current ? knoepfe.current.get(offenRef.current) : undefined;
      if (!knopf?.contains(event.target as Node)) schliessen();
    };
    document.addEventListener('keydown', taste, true);
    document.addEventListener('pointerdown', daneben, true);
    return () => {
      document.removeEventListener('keydown', taste, true);
      document.removeEventListener('pointerdown', daneben, true);
    };
  }, [offen, schliessen]);

  if (!zeilen.length) return <p className="ki-info">{leer}</p>;
  const sichtbarZeilen = zeilen.flatMap(z => z.tokens === null ? [] : [{ ...z, tokens: z.tokens }]);
  if (!sichtbarZeilen.length) return null;
  const prozente = balkenProzent(sichtbarZeilen.map(z => z.tokens));
  return <ul className="ki-token-liste" aria-label={PERSONEN.tokensTitel}>
    {sichtbarZeilen.map((z, i) => {
      const sichtbar = offen === z.key;
      return <li key={z.key} className="ki-token-zeile" data-key={z.key}>
        <button type="button" className="ki-token-knopf" aria-expanded={sichtbar} aria-describedby={sichtbar ? tippId : undefined}
          ref={knopf => { if (knopf) knoepfe.current.set(z.key, knopf); else knoepfe.current.delete(z.key); }}
          onPointerEnter={() => oeffnen(z.key)}
          // Nur die Maus verlässt eine Zeile; ein Finger hebt ab, der Tooltip bleibt bis daneben.
          onPointerLeave={event => { if (event.pointerType === 'mouse') schliessen(); }}
          onFocus={() => oeffnen(z.key)} onBlur={schliessen} onClick={() => oeffnen(z.key)}>
          <span className="ki-token-name">{z.name}</span>
          <span className="ki-token-spur" aria-hidden="true">
            {prozente[i] > 0 && <span className="ki-token-fuellung" style={{ width: `${prozente[i]}%` }} />}
          </span>
          <span className="ki-token-zahl">{tokenZahl(z.tokens)}</span>
          {!!z.tokensOhneAngabe && <small className="ki-token-hinweis">{PERSONEN.tokensOhneAngabe(z.tokensOhneAngabe)}</small>}
        </button>
        {sichtbar && <div id={tippId} role="tooltip" className="ki-tipp ki-token-tipp"><TippBox inhalt={tokenTipp(z)} /></div>}
      </li>;
    })}
  </ul>;
}
